/**
 * LEWIS DOTS: up to two electrons on each of an atom's four sides.
 *
 * ── WHY A MARK AND NOT A FRAME ────────────────────────────────────────────────────────────────
 * The owner's requirement was four positions with one or two electrons each; the shape was left
 * open, and a five-slot frame was sketched. A mark is what this editor already decided these
 * are: lone pairs WERE frames with a hole, and they became a mark because that is what they are
 * — "something added to an atom you already wrote, not something you insert and then fill".
 * Nothing about adding two more sides changes that.
 *
 * ── THE SPELLING IS OURS, AND IT HAD TO BE MEASURED ───────────────────────────────────────────
 * The scope recorded `{··}\overset{··}{\underset{··}{O}}{··}`. It does not render: a bare `{··}`
 * group inside `\ce{}` is rejected, which was found by trying it. What mhchem does accept is
 * `\cdot` for a single dot beside an atom, and an empty-based `\overset{.}{\underset{.}{…}}` for
 * a pair stacked vertically beside it, which is how a side pair is drawn.
 *
 * ── ONE GRAMMAR, READ AND WRITTEN IN ONE PLACE ────────────────────────────────────────────────
 * Both the document layer and the legacy token layer carry this notation, and a mutation has
 * already shown that two copies of a wrap can disagree without a test noticing. So the string
 * is built and read here, once, and both call in.
 *
 * ── AND IT STAYS BACKWARDS COMPATIBLE BY CONSTRUCTION ─────────────────────────────────────────
 * The writer omits every empty layer. A pair above and nothing else therefore comes out as
 * `$\overset{..}{\ce{O}}$` — byte for byte what the lone-pair control wrote before this existed
 * — and the reader takes that as the same thing. No migration, and no second parser for the old
 * shape: the old shape IS this grammar with three positions empty.
 */

/** How many electrons sit on each side. Clockwise from the top, which is how they are read. */
export type LewisDots = { above: number; right: number; below: number; left: number };

export const NO_DOTS: LewisDots = { above: 0, right: 0, below: 0, left: 0 };

export const LEWIS_SIDES = ['above', 'right', 'below', 'left'] as const;
export type LewisSide = (typeof LEWIS_SIDES)[number];

/**
 * ── HOW THE FOUR POSITIONS ARE DRAWN ──────────────────────────────────────────────────────────
 *
 * Every dot is the SAME SIZE and the SAME DISTANCE from the atom, whichever of the four sides
 * it is on. That is one sentence and it took four measured passes, because mhchem and KaTeX give
 * each position a different default and none of the four agreed with the others:
 *
 *   · a side PAIR is a colon. Two dots held at lone-pair spacing is what a colon already is, and
 *     no stacking construct gets near it — `\substack` is a two-ROW vlist and measured 0.6em
 *     apart beside a glyph only 0.68em tall, which is the top-and-bottom split this was first
 *     reported as. But a colon arrives at FULL size, where `\overset`'s dots are script size, so
 *     it has to be shrunk or it reads as bolder than the dots above it. Reported.
 *   · a side SINGLE is likewise `\scriptstyle`, so it matches the pair beside it.
 *   · both of them get `\mkern3mu` between the glyph and the atom. Without it the ink gap
 *     measured 0.15em and the electrons crowded the letter. Reported.
 *   · BELOW gets a `\raisebox`, because `\underset`'s gap is three times `\overset`'s: measured
 *     at a 40px atom, 8px above and 24px below. Reported as the lower pair sitting too low.
 *
 * Every number here was read off pixels, not guessed, and the invariants in the test file pin
 * them to each other rather than to their values.
 */
const SIDE_GAP = '\\mkern3mu';
/*
 * ONE CONSTRUCT FOR BOTH, differing only in the character: a colon is two dots at lone-pair
 * spacing, a full stop is one. Each sits on the text baseline, so each is raised onto the
 * atom's middle — further for the single, which has no upper dot to be averaged with. 0.2em and
 * 0.3em are what put each one's ink midline on `\ce{O}`'s, measured in pixels.
 *
 * `\cdot` would be the obvious glyph for the single and cannot be used: shrinking it needs a
 * brace group, and a `{` directly after the `$` that opens the wrap makes mhchem reject the
 * whole reaction. Found by rendering all eighty combinations, which is why that test exists.
 */
const PAIR_GLYPH = '\\raisebox{0.2em}{\\scriptsize:}';
const ONE_GLYPH = '\\raisebox{0.3em}{\\scriptsize.}';
const SIDE_PAIR_LEFT = `${PAIR_GLYPH}${SIDE_GAP}`;
const SIDE_PAIR_RIGHT = `${SIDE_GAP}${PAIR_GLYPH}`;
const SIDE_ONE_LEFT = `${ONE_GLYPH}${SIDE_GAP}`;
const SIDE_ONE_RIGHT = `${SIDE_GAP}${ONE_GLYPH}`;
/*
 * THE LIFT THAT BRINGS THE BOTTOM DOTS UP TO THE GAP THE TOP ONES SIT AT, and the `\scriptsize`
 * that keeps them the same SIZE while it does.
 *
 * `\raisebox` renders its contents in text mode at the full font size, which `\underset`'s own
 * script style would otherwise have shrunk. So the raise that fixed the gap made the dots half
 * again as tall as the pair above them: measured at a 96px reaction, 15px of ink below against
 * 10px above. Reported as the side electrons being heavier than the top and bottom ones — they
 * were not; the BOTTOM ones were heavier than everything.
 *
 * With the size put back, 0.30em is what makes the two gaps equal: 21px above and 21px below at
 * that same 96px, where `\underset` left alone gives 57px below against 21px above. Swept, not
 * solved — 0.32em overshoots to 19 and 0.34em to 17.
 */
const BELOW_LIFT_OPEN = '\\raisebox{0.30em}{\\scriptsize';
const lift = (dots: string) => `${BELOW_LIFT_OPEN}${dots}}`;

/**
 * Every spelling this wrote before, still read so nothing stored breaks.
 *
 * `\overset{.}{\underset{.}{\vphantom{X}}}` put one dot ABOVE the atom and one BELOW it, spanning
 * its full height; `\substack` halved that gap and no more. The singles were pulled IN with `\!`,
 * a negative thin space, on the reading that `\cdot`'s wide advance made the dot look adrift —
 * measured later, it put the dot INSIDE the letter's own ink.
 */
const SIDE_PAIR_STACK = '\\substack{.\\\\.}';
const SIDE_PAIR_TALL = '\\overset{.}{\\underset{.}{\\vphantom{X}}}';
const SIDE_ONE_TIGHT_LEFT = '\\cdot\\!';
const SIDE_ONE_TIGHT_RIGHT = '\\!\\cdot';
const SIDE_ONE_LOOSE = '\\cdot';
/*
 * The colon arrived before it was shrunk, so a reaction written in that window carries one at
 * FULL size and with no gap. It is in the owner's own content; reading it is what lets the
 * renderer re-spell it — see `upgradeLewis`.
 */
const SIDE_PAIR_RAISED = '\\raisebox{0.12em}{:}';
const SIDE_ONE_RAISED = '\\raisebox{0.12em}{.}';
/** The bottom lift before it was given its size back. */
const BELOW_LIFT_UNSIZED = '\\raisebox{0.28em}{';

const clamp = (n: number) => (n === 1 || n === 2 ? n : 0);

/** The four counts as a string, which is what the mark attribute and the DOM carry. */
export const lewisValue = (d: LewisDots): string =>
  `${clamp(d.above)}${clamp(d.right)}${clamp(d.below)}${clamp(d.left)}`;

export function parseLewisValue(value: string): LewisDots {
  const n = (i: number) => clamp(Number(value[i] ?? 0));
  return { above: n(0), right: n(1), below: n(2), left: n(3) };
}

export const hasDots = (d: LewisDots): boolean =>
  d.above > 0 || d.right > 0 || d.below > 0 || d.left > 0;

/** The dots a top or bottom position writes: they sit side by side there, not stacked. */
const stack = (count: number) => (count === 2 ? '..' : count === 1 ? '.' : '');
/* The gap goes on the side facing the atom, which is why each of these has two spellings. */
const side = (count: number, where: 'left' | 'right') =>
  (count === 2 ? (where === 'left' ? SIDE_PAIR_LEFT : SIDE_PAIR_RIGHT)
    : count === 1 ? (where === 'left' ? SIDE_ONE_LEFT : SIDE_ONE_RIGHT)
      : '');

/**
 * The whole wrap, with every empty layer left out.
 *
 * Inside out: the species, then under, then over, then the two sides. Leaving the empty layers
 * out is what keeps a pair above spelled exactly as the lone-pair control used to spell it.
 */
export function writeLewis(inner: string, dots: LewisDots): string {
  let out = `\\ce{${inner}}`;
  if (clamp(dots.below)) out = `\\underset{${lift(stack(dots.below))}}{${out}}`;
  if (clamp(dots.above)) out = `\\overset{${stack(dots.above)}}{${out}}`;
  return `$${side(dots.left, 'left')}${out}${side(dots.right, 'right')}$`;
}

/** A dots group's contents, which is what tells a Lewis wrap from an oxidation number. */
const DOTS_ONLY = /^\.{1,2}$/;

/**
 * The dots inside an `\overset` or `\underset` group, or null when the group holds something
 * else — an oxidation number, say, which wears the same two commands.
 *
 * The BELOW group arrives wrapped in the lift; the ABOVE group never is. Unwrapping here rather
 * than at the two call sites is what keeps the reader blind to which side it is on.
 */
function dotsOf(group: string): string | null {
  let bare = group;
  for (const open of [BELOW_LIFT_OPEN, BELOW_LIFT_UNSIZED]) {
    if (group.startsWith(open) && group.endsWith('}')) { bare = group.slice(open.length, -1); break; }
  }
  return DOTS_ONLY.test(bare) ? bare : null;
}

/** The index just past the brace group opening at `from`, or -1. */
function closeOfBrace(src: string, from: number): number {
  if (src[from] !== '{') return -1;
  let depth = 0;
  for (let i = from; i < src.length; i++) {
    if (src[i] === '{') depth++;
    else if (src[i] === '}' && --depth === 0) return i + 1;
  }
  return -1;
}

/**
 * A side's dots at `at`, and where they end.
 *
 * LONGEST FIRST, and here it is load-bearing: `\cdot` is a prefix of `\cdot\!`, so reading the
 * loose spelling first would stop short and leave a stray `\!` to be read as something else.
 * (It was not load-bearing while the only forms were an `\overset` and a `\cdot`, and a
 * mutation said so at the time — the tightening is what gave the two a shared prefix.)
 */
function readSide(src: string, at: number): { count: number; end: number } {
  const pairs = [SIDE_PAIR_LEFT, SIDE_PAIR_RIGHT, SIDE_PAIR_STACK, SIDE_PAIR_TALL,
    SIDE_PAIR_RAISED];
  const ones = [SIDE_ONE_LEFT, SIDE_ONE_RIGHT, SIDE_ONE_TIGHT_LEFT, SIDE_ONE_TIGHT_RIGHT,
    SIDE_ONE_RAISED, SIDE_ONE_LOOSE];
  for (const form of [...pairs, ...ones]) {
    if (src.startsWith(form, at)) {
      return { count: pairs.includes(form) ? 2 : 1, end: at + form.length };
    }
  }
  return { count: 0, end: at };
}

/**
 * Read a Lewis wrap starting at `from`, or null when there is not one there.
 *
 * Returns the species SOURCE rather than parsing it, so the caller reads it with whatever it
 * uses for the rest of the reaction — the document layer and the token layer disagree about
 * what a species is, and neither of them is this module's business.
 */
export function readLewis(src: string, from: number): {
  dots: LewisDots; inner: string; end: number;
} | null {
  if (src[from] !== '$') return null;
  let i = from + 1;

  const left = readSide(src, i);
  i = left.end;

  let above = 0;
  let below = 0;
  /* Each wrapper opens a brace that has to be closed again after the species, so they are
     counted on the way in and matched on the way out. */
  const opened: number[] = [];

  for (const [command, set] of [['\\overset', 'above'], ['\\underset', 'below']] as const) {
    if (!src.startsWith(command, i)) continue;
    const dotsEnd = closeOfBrace(src, i + command.length);
    if (dotsEnd < 0) return null;
    const dots = dotsOf(src.slice(i + command.length + 1, dotsEnd - 1));
    if (dots === null) return null;
    if (src[dotsEnd] !== '{') return null;
    if (set === 'above') above = dots.length; else below = dots.length;
    opened.push(dotsEnd);
    i = dotsEnd + 1;
  }

  if (!src.startsWith('\\ce{', i)) return null;
  const ceEnd = closeOfBrace(src, i + '\\ce'.length);
  if (ceEnd < 0) return null;
  const inner = src.slice(i + '\\ce{'.length, ceEnd - 1);
  i = ceEnd;

  /* One closing brace per wrapper opened above. */
  for (let k = 0; k < opened.length; k++) {
    if (src[i] !== '}') return null;
    i++;
  }

  const right = readSide(src, i);
  i = right.end;

  if (src[i] !== '$') return null;
  const dots: LewisDots = { above, right: right.count, below, left: left.count };
  /* `$\ce{O}$` is not a Lewis wrap, it is a species in maths mode. */
  if (!hasDots(dots)) return null;
  return { dots, inner, end: i + 1 };
}

/**
 * EVERY LEWIS WRAP IN A SOURCE, RE-SPELLED IN TODAY'S GRAMMAR.
 *
 * The notation is stored, so a reaction written last week keeps the spelling that was current
 * last week — and this grammar has been measured and corrected twice. The owner's own content
 * carries a full-size colon from the window before the colon was shrunk, and nothing would ever
 * have fixed it: `writeLewis` only runs when a mark is applied, which is not something a teacher
 * does again to a reaction that already has one.
 *
 * So the renderer re-spells on the way to the screen, which is how `remapInkColors` and the
 * reaction's accessible name already work and for the same reason — derived at display, it is
 * always current and it heals what is already stored without touching it.
 *
 * A no-op on anything written in the current spelling, by construction: `readLewis` returns what
 * `writeLewis` put there and `writeLewis` puts it back.
 */
export function upgradeLewis(src: string): string {
  if (!src.includes('$')) return src;
  let out = '';
  for (let i = 0; i < src.length;) {
    const read = src[i] === '$' ? readLewis(src, i) : null;
    if (!read) { out += src[i]; i++; continue; }
    out += writeLewis(read.inner, read.dots);
    i = read.end;
  }
  return out;
}
